import { ALL_GRADES, HUB_PROGRAMS } from 'app/constants/entitlement.constant'
import { appLinks } from 'app/constants/home-content-buttons.constants'
import {
  HOME_LINKS_ID,
  PROGRAM_FILTERS,
} from 'app/constants/home-links.constants'
import { useAppSelector } from 'app/store'
import { GRADES } from 'app/types/grades.types'
import {
  HomeContentButton,
  ProgramFilter,
} from 'app/types/home-content-button.types'
import { cloneDeep, uniq } from 'lodash'

import { useProgramContext } from '../contexts/program/program.context'
import {
  useIsCKLAHybridUser,
  useIsCaminosHybridUser,
} from './use-is-hybrid-user.hook'
import useReadOnly from './use-read-only.hook'

export default function useFilteredAppLinks(): Record<
  GRADES,
  Record<string, HomeContentButton[]>
> {
  const isReadOnly = useReadOnly()
  const filteredAppLinks = cloneDeep(appLinks)
  const { filterValue } = useProgramContext()

  const filtersByGrade = useAppSelector((state) => state.userData.filters)
  const isFreeAccessUser = useAppSelector(
    (state) => state.userData.kcInfo.isFreeAccess
  )

  useAppSelector((state) => state.userData)

  const removeAppFromGrade = (
    gradeAppLinks: Record<string, { id: string }[]>,
    program: string,
    app: string
  ) => {
    gradeAppLinks[program] = gradeAppLinks[program].filter(
      (appEntry) => appEntry.id !== app
    )
    return gradeAppLinks
  }
  const removeAppFromAllGrades = (program: ProgramFilter, appId: string) => {
    Object.keys(appLinks).forEach((key) => {
      removeAppFromGrade(
        filteredAppLinks[key as keyof typeof appLinks],
        program,
        appId
      )
    })
    return filteredAppLinks
  }

  // go through all grades
  ALL_GRADES.forEach((grade) => {
    // go through user's filters for every grade
    const gradeFilters = filteredAppLinks[grade]
    Object.keys(gradeFilters).forEach((filter) => {
      // and check if user has filter for this grade
      if (filtersByGrade[grade] && filtersByGrade[grade].includes(filter)) {
        // if so, add it to All Apps filter
        gradeFilters[PROGRAM_FILTERS.ALL_APPS] = [
          ...(gradeFilters[PROGRAM_FILTERS.ALL_APPS] || []),
          ...gradeFilters[filter],
        ]
      }
    })
    gradeFilters[PROGRAM_FILTERS.ALL_APPS] = uniq(
      gradeFilters[PROGRAM_FILTERS.ALL_APPS]
    )
  })

  if (
    useIsCKLAHybridUser(HUB_PROGRAMS.CAMINOS) &&
    filterValue === PROGRAM_FILTERS.ALL_APPS
  ) {
    removeAppFromAllGrades(PROGRAM_FILTERS.ALL_APPS, HOME_LINKS_ID.BIBLIOTECA)
  }

  if (
    useIsCKLAHybridUser(HUB_PROGRAMS.TEXAS_ELAR) &&
    filterValue === PROGRAM_FILTERS.ALL_APPS
  ) {
    removeAppFromAllGrades(PROGRAM_FILTERS.ALL_APPS, HOME_LINKS_ID.TEXAS_ELAR)
  }

  if (
    useIsCaminosHybridUser(HUB_PROGRAMS.TEXAS_SLAR) &&
    filterValue === PROGRAM_FILTERS.ALL_APPS
  ) {
    removeAppFromAllGrades(PROGRAM_FILTERS.ALL_APPS, HOME_LINKS_ID.TEXAS_SLAR)
  }

  if (
    useIsCKLAHybridUser(HUB_PROGRAMS.TEXAS_ELAR_2E) &&
    filterValue === PROGRAM_FILTERS.ALL_APPS
  ) {
    removeAppFromAllGrades(
      PROGRAM_FILTERS.ALL_APPS,
      HOME_LINKS_ID.TEXAS_ELAR_2E
    )
  }

  if (
    useIsCaminosHybridUser(HUB_PROGRAMS.TEXAS_SLAR_2E) &&
    filterValue === PROGRAM_FILTERS.ALL_APPS
  ) {
    removeAppFromAllGrades(
      PROGRAM_FILTERS.ALL_APPS,
      HOME_LINKS_ID.TEXAS_SLAR_2E
    )
  }

  if (isReadOnly) {
    removeAppFromAllGrades(PROGRAM_FILTERS.CKLA3, HOME_LINKS_ID.VOCAB)
    removeAppFromAllGrades(PROGRAM_FILTERS.CKLA, HOME_LINKS_ID.VOCAB)
    removeAppFromAllGrades(PROGRAM_FILTERS.ALL_APPS, HOME_LINKS_ID.VOCAB)
  }

  if (isFreeAccessUser) {
    removeAppFromAllGrades(PROGRAM_FILTERS.CKLA3, HOME_LINKS_ID.VOCAB)
  }

  return filteredAppLinks
}
